import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import Modal from '../components/Modals/Modal/Modal';
import ForceErrorModalContent from '../components/Modals/ForceErrorModalContent/ForceErrorModalContent';
import { ForceErrorAnswer } from '../types/types';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('forceErrors');

export type ForceErrorContextType = {
  openForceErrorModal: (
    previousForceHeader: string | undefined,
    message: string,
    answers: ForceErrorAnswer[],
    forceModalAction: (forceHeader: string) => void,
    cancelAction?: () => void
  ) => void;
  // Exposed so loading/error UIs can hide themselves while the modal is up
  isForceModalOpen: boolean;
};

type forceActionFn = (
  header: string
) => void | ((header: string) => Promise<any>);

type cancelActionFn = () => void | (() => Promise<any>);

interface ForceErrorContextProviderProps {
  children: ReactNode;
}

const defaultValues: ForceErrorContextType = {
  openForceErrorModal: () => {
    throw new Error('openForceErrorModal must be implemented');
  },
  isForceModalOpen: false,
};

const ForceErrorContext = createContext<ForceErrorContextType>(defaultValues);

const DEFAULT_ACTION = () => () => {};

const ForceErrorModalContextProvider = ({
  children,
}: ForceErrorContextProviderProps) => {
  const [forceModalOpen, setForceModalOpen] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [answers, setAnswers] = useState<ForceErrorAnswer[]>([]);
  const [action, setAction] = useState<forceActionFn>(DEFAULT_ACTION);
  const [cancelAction, setCancelAction] =
    useState<cancelActionFn>(DEFAULT_ACTION);
  const promiseRef = useRef<{
    promise: Promise<any>;
    resolve: (value: any) => void;
  } | null>(null);

  const openForceErrorModal = useCallback(
    (
      previousForceHeader,
      message,
      answers: ForceErrorAnswer[],
      forceModalAction,
      forceModalCancelAction?: () => void
    ) => {
      /** @debugFeature forceErrors Logs an opened force modal: the question, offered answer values and whether it chains onto previous answers. */
      log('force modal opened', () => ({
        message,
        answers: answers.map((answer) => answer.value),
        chained: Boolean(previousForceHeader),
        hasCancelAction: Boolean(forceModalCancelAction),
      }));

      setForceModalOpen(true);
      setMessage(message);
      setAnswers(answers);
      setAction(
        () => (header) =>
          forceModalAction(
            previousForceHeader ? `${previousForceHeader},${header}` : header
          )
      );
      // Always reset so a chained modal without its own cancel action doesn't
      // inherit the previous one.
      setCancelAction(() => forceModalCancelAction ?? DEFAULT_ACTION);
      // First force error creates the promise; chained errors reuse it so
      // every nested handleApiError resolves together at final close instead
      // of awaiting an orphaned promise that never settles.
      if (!promiseRef.current) {
        let resolve!: (value: any) => void;
        const promise = new Promise((res) => {
          resolve = res;
        });
        promiseRef.current = { promise, resolve };
      }
      return promiseRef.current.promise;
    },
    []
  );

  const closeForceErrorModal = useCallback((response: any = null) => {
    setForceModalOpen(false);
    setMessage('');
    setAnswers([]);
    setAction(DEFAULT_ACTION);
    setCancelAction(DEFAULT_ACTION);
    promiseRef.current?.resolve(response);
    promiseRef.current = null;
  }, []);

  const forceModalAction: (value: string) => Promise<void> = useCallback(
    async (value: string) => {
      /** @debugFeature forceErrors Logs the user's force answer that is retried with the accumulated X-ASW-FORCE value. */
      log('force answer submitted', () => ({ answer: value }));

      const forceApiRes = await action(value);
      // Only the final close resolves the shared promise; nested calls unwinding
      // after the chain already resolved find promiseRef cleared and skip.
      if (promiseRef.current) closeForceErrorModal(forceApiRes);
    },
    [action]
  );

  const cancelModalAction: () => Promise<void> = useCallback(async () => {
    /** @debugFeature forceErrors Logs a cancelled force modal — the awaiting caller resolves with null. */
    log('force modal cancelled');

    if (cancelAction) await cancelAction();
    closeForceErrorModal();
  }, [cancelAction, closeForceErrorModal]);

  const context = useMemo(
    () => ({
      openForceErrorModal,
      isForceModalOpen: forceModalOpen,
    }),
    [openForceErrorModal, forceModalOpen]
  );

  return (
    <ForceErrorContext.Provider value={context}>
      {forceModalOpen && (
        <Modal
          isOpen
          onClose={() => setForceModalOpen(false)}
          content={
            <ForceErrorModalContent
              text={message}
              forceModalAction={forceModalAction}
              cancelModalAction={cancelModalAction}
              buttons={answers}
            />
          }
        />
      )}
      {children}
    </ForceErrorContext.Provider>
  );
};

export const useForceErrorModal = () => {
  const context = useContext(ForceErrorContext);

  if (context === undefined) {
    throw new Error(
      'useForceErrorModal must be used within a DatasetFormDataContextProvider'
    );
  }

  return context;
};

export default ForceErrorModalContextProvider;
